import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/AuthContext";
import axiosInstance from "../../axiosInstace";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import HrmImage from "../../assets/hrlabs-logo.png";

const MarketplaceSidebar = () => {
  // --- STATE MANAGEMENT ---
  const { hasRole } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCeoPrompt, setShowCeoPrompt] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [collapsed, setCollapsed] = useState(
    () => sessionStorage.getItem("marketplaceCollapsed") === "true",
  );

  const tenantId = sessionStorage.getItem("tenantId");
  const currentHostname = window.location.hostname;

  const EXPANDED_WIDTH = "50px";
  const COLLAPSED_WIDTH = "0px";

  // Keep --marketplace-width in sync at runtime, same pattern Sidebar.jsx
  // uses for --sidebar-width, so .main-content always reserves the
  // correct amount of space.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--marketplace-width",
      collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
    );
    sessionStorage.setItem("marketplaceCollapsed", String(collapsed));
  }, [collapsed]);

  // Reset to the default expanded width if this component unmounts
  // (e.g. navigating to a route without the marketplace) so other
  // pages aren't left with a stale collapsed value.
  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty(
        "--marketplace-width",
        EXPANDED_WIDTH,
      );
    };
  }, []);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let apiUrl = "/api/products/all";
        if (tenantId) {
          apiUrl += `?tenantId=${tenantId}`;
        }
        const productsResponse = await axiosInstance.get(apiUrl);

        if (productsResponse.data && productsResponse.data.data) {
          const correctedProducts = productsResponse.data.data.map((p) => ({
            ...p,
            productLink: p.productLink.replace(/^https:\//, "https://"),
          }));
          setProducts(correctedProducts);
        } else {
          setError(productsResponse.data.message || "Failed to fetch products");
        }
      } catch (err) {
        setError("An error occurred while fetching data.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId]);

  // --- EVENT HANDLERS ---
  const handleProductClick = (product) => {
    if (product.purchased) {
      let redirectUrl = product.productLink;
      if (tenantId) {
        redirectUrl += `${
          redirectUrl.includes("?") ? "&" : "?"
        }tenantId=${tenantId}`;
      }
      window.location.href = redirectUrl;
    } else {
      setSelectedProduct(product);
      if (hasRole("ROLE_CEO")) {
        setShowCeoPrompt(true);
      } else {
        setShowUpgradeModal(true);
      }
    }
  };

  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
    setSelectedProduct(null);
  };

  const handleCloseCeoPrompt = () => {
    setShowCeoPrompt(false);
    setSelectedProduct(null);
  };

  const handlePurchase = () => {
    window.location.href = "https://www.ourdept.com";
  };

  const handleMouseEnter = (itemName, event) => {
    setHoveredItem(itemName);

    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();

      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.left - 16, // space between icon and tooltip
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setTooltipPosition({ top: 0 });
  };

  // --- UTILITY ---
  const getIconName = (url) => {
    return url && url.startsWith("icon: ") ? url.substring(6) : url;
  };

  // --- RENDER LOGIC ---
  if (loading) return <LoadingScreen />;

  if (error) {
    console.error("Marketplace Sidebar Error:", error);
    return null;
  }

  // Tooltip component with high z-index
  const Tooltip = ({ title, description, position }) => (
    <div
      className="fixed z-[999] w-[260px] p-4 bg-[#0f172a] text-white rounded-xl shadow-2xl pointer-events-none transition-all duration-200"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "translate(-100%, -50%)",
      }}
    >
      <p className="font-semibold text-base mb-1">{title}</p>
      {description && (
        <p className="text-sm text-slate-300 leading-relaxed">{description}</p>
      )}

      {/* Arrow pointing RIGHT (towards icon) */}
      <div
        className="absolute top-1/2 -translate-y-1/2 right-[-8px] w-0 h-0
      border-t-[8px] border-t-transparent
      border-b-[8px] border-b-transparent
      border-l-[8px] border-l-[#0f172a]"
      />
    </div>
  );

  return (
    <>
      {/* Collapse / expand toggle — stays visible in both states, docked
          to the left edge of wherever the sidebar currently sits. */}
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Expand marketplace" : "Collapse marketplace"}
        aria-expanded={!collapsed}
        className="
    fixed top-[calc(var(--navbar-height)+12px)] z-[101]
    flex lg:hidden
    items-center justify-center
    w-6 h-6 rounded-full
    bg-[var(--bg-primary)]
    border border-[var(--border-secondary)]
    text-[var(--text-muted)]
    shadow-sm
    hover:text-[var(--active-color)]
    hover:bg-[var(--bg-hover)]
    transition-all duration-200
  "
        style={{
          right: collapsed ? "4px" : "calc(var(--marketplace-width) - 12px)",
        }}
      >
        <Icon
          icon={
            collapsed
              ? "solar:alt-arrow-left-linear"
              : "solar:alt-arrow-right-linear"
          }
          className="w-4 h-4"
        />
      </button>

      {/* Sidebar Navigation */}
      <nav
        aria-hidden={collapsed}
        className={`fixed top-[var(--navbar-height)] right-0 h-[calc(100dvh-var(--navbar-height))] w-[var(--marketplace-width)]
bg-[var(--bg-primary)] border-l border-[var(--border-secondary)] z-[100]
flex flex-col items-center pt-4 gap-4 overflow-y-auto overflow-x-hidden
transition-all duration-200 ${collapsed ? "px-0 pointer-events-none opacity-0" : "px-2 opacity-100"}`}
        style={{ boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)" }}
      >
        {/* Dynamic Product Icons */}
        {products.map((product) => {
          let productHostname = "";
          try {
            productHostname = new URL(product.productLink).hostname;
          } catch (e) {
            console.error("Invalid product link URL:", product.productLink);
          }
          const isActive = currentHostname === productHostname;

          return (
            <div key={product.productCode} className="flex justify-center">
              <div
                onClick={() => handleProductClick(product)}
                onMouseEnter={(event) =>
                  handleMouseEnter(product.productName, event)
                }
                onMouseLeave={handleMouseLeave}
                className={`flex items-center justify-center w-10 h-10 text-2xl rounded-lg cursor-pointer transition-colors duration-200 ease-in-out
${
  isActive
    ? "bg-[var(--active-color)] text-white"
    : "text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--active-color)]"
}`}
              >
                {product.productIconUrl ? (
                  <Icon icon={getIconName(product.productIconUrl)} />
                ) : (
                  <img
                    src={HrmImage}
                    alt="HRLabs"
                    className="w-9 h-9 object-contain"
                  />
                )}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Tooltips - Rendered with high z-index */}
      {hoveredItem === "HRM" && (
        <Tooltip title="HRM" position={tooltipPosition} />
      )}
      {products.map(
        (product) =>
          hoveredItem === product.productName && (
            <Tooltip
              key={`tooltip-${product.productCode}`}
              title={product.productName}
              description={product.description}
              position={tooltipPosition}
            />
          ),
      )}

      {/* --- CEO-Specific Prompt Modal --- */}
      {showCeoPrompt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-[10001]"
          onClick={handleCloseCeoPrompt}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md m-4 p-6 text-center transform transition-all duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <Icon
                  icon="solar:lock-keyhole-minimalistic-bold"
                  className="w-7 h-7 text-blue-600 dark:text-blue-400"
                />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Product Not Available
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 px-4">
              Please ask the company to buy this product.
            </p>
            <button
              onClick={handleCloseCeoPrompt}
              type="button"
              className="w-full text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* --- Standard Upgrade Plan Modal --- */}
      {showUpgradeModal && selectedProduct && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-[10001]"
          onClick={handleCloseUpgradeModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl m-4 transform transition-all duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-2 border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-semibold text-gray-900">
                Upgrade to Unlock {selectedProduct.productName}
              </h2>
              <button
                onClick={handleCloseUpgradeModal}
                className="text-gray-400 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-black rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                aria-label="Close modal"
              >
                <Icon icon="ic:round-close" className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-1 space-y-6">
              <div className="flex items-center gap-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 text-3xl text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                  {selectedProduct.productIconUrl && (
                    <Icon icon={getIconName(selectedProduct.productIconUrl)} />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                    {selectedProduct.productName}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedProduct.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer with Purchase Button */}
            <div className="flex items-center justify-end p-2 space-x-4 rounded-xl">
              <button
                onClick={handleCloseUpgradeModal}
                type="button"
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-xl border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-black dark:hover:bg-gray-600 dark:focus:ring-gray-600 transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={handlePurchase}
                type="button"
                className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-xl text-sm px-8 py-2.5 text-center dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-800 transition-colors flex items-center gap-2"
              >
                <Icon icon="solar:cart-large-4-bold" className="w-5 h-5" />
                Purchase Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceSidebar;
